import React, { useEffect, useState } from "react";
import { Check, Headset, Loader2, PhoneCall, Play, Volume2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { api, unwrapObject } from "@/lib/api";
import { PageHeader, StatusPill } from "@/components/patterns";
import { useSoftphone } from "@/components/calling/SoftphoneContext";
import { cn } from "@/lib/utils";
import { dateLocale, withClock } from "@/lib/dateTime";

type Mic = { state: "unknown" | "checking" | "ok" | "blocked"; name?: string; message?: string };

function Step({ ok, title, detail, action }: { ok: boolean | null; title: string; detail: string; action?: React.ReactNode }) {
  return (
    <div className="flex items-start gap-2.5 border-t border-border/60 py-2.5 first:border-t-0">
      <span className={cn("mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full", ok === true ? "bg-success-soft text-success" : ok === false ? "bg-warning-soft text-warning" : "bg-muted text-muted-foreground")}>
        {ok === true ? <Check className="size-3" strokeWidth={3} /> : ok === false ? <X className="size-3" strokeWidth={3} /> : <Loader2 className="size-3 animate-spin" />}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-[13px] font-semibold">{title}</span>
        <span className="block text-xs text-muted-foreground">{detail}</span>
      </span>
      {action}
    </div>
  );
}

const Card = ({ title, aside, children }: { title: string; aside?: React.ReactNode; children: React.ReactNode }) => (
  <section className="flex flex-col gap-2.5 rounded-xl border border-border bg-card px-4 py-3.5">
    <div className="flex items-center justify-between gap-2">
      <h2 className="text-[11.5px] font-semibold uppercase tracking-wide text-muted-foreground">{title}</h2>
      {aside}
    </div>
    {children}
  </section>
);

/** Settings → My calling: every rep checks their own headset and phone line, and makes a test call before dialling a customer */
export function MyCallingPage({ embedded = false }: { embedded?: boolean } = {}) {
  const sp = useSoftphone();
  const [mic, setMic] = useState<Mic>({ state: "unknown" });
  const [mobile, setMobile] = useState("");
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [testing, setTesting] = useState(false);
  const [beeping, setBeeping] = useState(false);
  const [last, setLast] = useState<{ started_at: string; call_status: string; duration_sec: number | null } | null>(null);

  const loadLast = () =>
    api
      .get("/api/Call/LastTest")
      .then((r) => setLast(unwrapObject<typeof last>(r)))
      .catch(() => undefined);
  useEffect(() => {
    void loadLast();
  }, [sp.phase === "ended"]);

  /** Plays a short tone through the speakers so the rep can check they hear the browser */
  const beep = async () => {
    setBeeping(true);
    try {
      const ctx = new AudioContext();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      gain.gain.value = 0.15;
      osc.frequency.value = 660;
      osc.connect(gain).connect(ctx.destination);
      osc.start();
      await new Promise((r) => setTimeout(r, 900));
      osc.stop();
      await ctx.close();
    } finally {
      setBeeping(false);
    }
  };

  const checkMic = async () => {
    setMic({ state: "checking" });
    try {
      if (!navigator.mediaDevices?.getUserMedia) throw Object.assign(new Error("secure"), { name: "Insecure" });
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const name = stream.getAudioTracks()[0]?.label || "Default microphone";
      stream.getTracks().forEach((t) => t.stop());
      setMic({ state: "ok", name });
    } catch (err: any) {
      setMic({
        state: "blocked",
        message:
          err?.name === "NotFoundError" ? "No microphone found. Plug in a headset and check again." : err?.name === "Insecure" ? "This browser can't use a microphone here. Open the CRM from its usual secure link in an up-to-date browser." : "The microphone is blocked. Click the lock icon next to the address, allow the microphone for this site, then check again.",
      });
    }
  };

  useEffect(() => {
    void checkMic();
  }, []);

  const saveMobile = async () => {
    setSaving(true);
    setSaveError(null);
    setSaved(false);
    try {
      await sp.saveMobile(mobile.trim());
      setMobile("");
      setSaved(true);
    } catch (err: any) {
      setSaveError(err?.message || "Couldn't save the number. Check it and try again.");
    } finally {
      setSaving(false);
    }
  };

  const test = async () => {
    setTesting(true);
    try {
      await sp.startTestCall();
    } finally {
      setTesting(false);
    }
  };

  const enabled = sp.phase !== "off";
  const line = sp.phase === "ready" || sp.phase === "dialing" || sp.phase === "ringing" || sp.phase === "live" || sp.phase === "ended" ? true : sp.phase === "offline" ? false : null;
  const allGood = enabled && mic.state === "ok" && line === true && sp.hasMobile;
  const inCall = sp.phase === "dialing" || sp.phase === "ringing" || sp.phase === "live";

  return (
    <div className="space-y-4">
      {!embedded && <PageHeader title="My calling" />}
      <p className="text-[13px] text-muted-foreground">Check that calling works for you, and make a test call before you dial a customer.</p>

      <div className="flex flex-wrap items-start gap-3.5">
        <div className="flex min-w-0 flex-[1_1_440px] flex-col gap-3.5">
          <Card title="Ready check" aside={<StatusPill tone={allGood ? "success" : "warning"}>{allGood ? "All good" : "Needs attention"}</StatusPill>}>
            <Step ok={enabled} title="Calling is switched on for you" detail={enabled ? "Your admin enabled calling for your account." : "Ask your admin to switch calling on for you (Settings → Calling)."} />
            <Step
              ok={mic.state === "ok" ? true : mic.state === "blocked" ? false : null}
              title="Microphone found"
              detail={mic.state === "ok" ? mic.name || "" : mic.state === "blocked" ? mic.message || "" : "Checking…"}
              action={
                <Button type="button" variant="outline" size="sm" className="h-7" onClick={checkMic} disabled={mic.state === "checking"}>
                  Check again
                </Button>
              }
            />
            <Step
              ok={enabled ? line : false}
              title="Connected to the phone line"
              detail={!enabled ? "Needs calling switched on." : sp.phase === "connecting" ? "Signing in…" : line ? "You're signed in to the phone service." : sp.problem || "The browser couldn't sign in to the phone service. Reload the page; if it keeps happening, tell your admin."}
            />
            <Step ok={sp.hasMobile} title="Your mobile number" detail={sp.hasMobile ? "Saved. Test calls ring it." : "Add it below. Test calls ring this number."} />
          </Card>

          <Card title="Your mobile number">
            <div className="flex items-end gap-2.5">
              <div className="min-w-0 flex-1 space-y-1">
                <Label htmlFor="my-mobile" className="text-[12.5px] font-medium">
                  {sp.hasMobile ? "Change your mobile number" : "Mobile"}
                </Label>
                <Input id="my-mobile" type="tel" value={mobile} onChange={(e) => setMobile(e.target.value)} onKeyDown={(e) => e.key === "Enter" && mobile.trim() && saveMobile()} placeholder="+91 98765 43210" autoComplete="tel" className="font-mono" />
                <p className="text-[11.5px] text-muted-foreground">Test calls ring this number. Include the country code.</p>
              </div>
              <Button type="button" onClick={saveMobile} disabled={saving || !mobile.trim()}>
                {saving ? <Loader2 className="size-4 animate-spin" /> : "Save"}
              </Button>
            </div>
            {saveError && <p role="alert" className="text-xs text-destructive">{saveError}</p>}
            {saved && <p className="text-xs text-success">Saved.</p>}
          </Card>
        </div>

        <div className="flex min-w-0 flex-[1_1_340px] flex-col gap-3.5">
          <Card title="Test call">
            <p className="text-[13px] text-muted-foreground">We ring your mobile and connect you through the browser, so you can hear your headset, the other side and the recording notice. Nothing is logged against a customer.</p>
            <div className="flex flex-wrap gap-2">
              <Button type="button" className="bg-sidebar text-white hover:bg-sidebar/90" onClick={test} disabled={!sp.canTestCall || testing || inCall}>
                {testing ? <Loader2 className="mr-1.5 size-4 animate-spin" /> : <PhoneCall className="mr-1.5 size-4" />}
                Test call to my mobile
              </Button>
              <Button type="button" variant="outline" onClick={beep} disabled={beeping}>
                {beeping ? <Loader2 className="mr-1.5 size-4 animate-spin" /> : <Volume2 className="mr-1.5 size-4" />}
                Test speakers
              </Button>
            </div>
            {last?.started_at && (
              <div className="rounded-lg bg-panel px-3 py-2 text-[12.5px]">
                <strong>Last test</strong>
                <span className="block text-muted-foreground">
                  {new Date(last.started_at).toLocaleString(dateLocale(), withClock({ day: "numeric", month: "short", hour: "numeric", minute: "2-digit" }))} ·{" "}
                  {last.call_status === "COMPLETED" || last.call_status === "ANSWERED" ? `connected${last.duration_sec ? ` · ${last.duration_sec} s` : ""}` : last.call_status === "NO_ANSWER" ? "not answered" : last.call_status === "FAILED" ? "failed" : "not connected"}
                </span>
              </div>
            )}
            {!sp.canTestCall && !inCall && <p className="text-xs text-muted-foreground">{!enabled ? "Calling isn't switched on for you yet." : !sp.hasMobile ? "Add your mobile number first." : "Waiting for the phone line to connect."}</p>}
            {inCall && <p className="text-xs text-muted-foreground">The call is in progress. Use the call chip in the top bar to mute or end it.</p>}
            {sp.problem && sp.phase !== "offline" && <p role="alert" className="text-xs text-destructive">{sp.problem}</p>}
          </Card>
          <Card title="Incoming calls on this device">
            <div className="flex items-center gap-3">
              <span className="min-w-0 flex-1">
                <span className="block text-[13px] font-semibold">Play the ringtone</span>
                <span className="block text-xs text-muted-foreground">When a customer calls the shared number and it rings you. The sound is chosen by your admin.</span>
              </span>
              <Switch checked={sp.ringtone.on} onCheckedChange={sp.setRingtoneOn} aria-label="Play the ringtone on this device" />
            </div>
            <div className="flex items-center gap-3 border-t border-border/60 pt-2.5">
              <Volume2 className="size-4 text-muted-foreground" />
              <input type="range" min={0} max={100} value={sp.ringtone.volume} onChange={(e) => sp.setRingtoneVolume(Number(e.target.value))} aria-label="Ringtone volume on this device" className="h-1 flex-1 cursor-pointer accent-primary" />
              <span className="w-10 text-right font-mono text-xs">{sp.ringtone.volume}%</span>
              <Button type="button" variant="outline" size="sm" onClick={sp.testRingtone}>
                <Play className="mr-1.5 size-3.5" />
                Test
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">If the CRM is in another tab, you also get a desktop notification once your browser allows it (it asks the first time you click in the CRM).</p>
          </Card>
          <Card title="Good to know">
            <ul className="flex flex-col gap-1.5 text-[13px] text-muted-foreground">
              <li className="flex gap-2"><Headset className="mt-0.5 size-4 shrink-0" /> Use a headset to avoid echo.</li>
              <li className="flex gap-2"><PhoneCall className="mt-0.5 size-4 shrink-0" /> Customers are dialled from the Call buttons on leads, contacts, accounts and deals.</li>
              <li className="flex gap-2"><Check className="mt-0.5 size-4 shrink-0" /> Every call you make is on the Calls page, with its recording when recording is on.</li>
            </ul>
          </Card>
        </div>
      </div>
    </div>
  );
}
